import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { DEFAULT_WORKING_WEEKDAYS } from '@obraplan/core/domain/calendar/constants';
import { BusinessDate } from '@obraplan/core/domain/date/business-date';
import { itemProgress } from '@obraplan/core/domain/takeoff/room-progress';
import { formatBusinessDate } from '@/lib/format';

/**
 * O que a tela do cronograma por item calcula sobre os itens (Fase 25).
 *
 * Tudo aqui é conta de exibição — dias úteis, equipe implícita, agrupamento, escala do
 * Gantt. Nada decide regra: a ordem dos itens, a linha de base e o avanço vêm prontos do
 * servidor (`ListScheduleItems`, RN-320 a RN-322), e o componente só os desenha.
 */

/** Jornada de referência da equipe implícita: o dia de 8 horas do GanttPRO. */
export const HOURS_PER_DAY = 8;

/**
 * Dias úteis entre duas datas, inclusive nas duas pontas — segunda a sexta.
 *
 * Sem feriado: o calendário da obra é lido por quem tem `employees.view`, e esta tela abre
 * só com `schedule.view`. É o mesmo "dia útil" do arquivo, que conta a semana de cinco dias.
 * Datas inválidas ou invertidas valem zero.
 */
export function businessDaysBetween(start: string, end: string): number {
  const from = BusinessDate.parse(start);
  const to = BusinessDate.parse(end);
  if (!from.ok || !to.ok) return 0;

  const total = from.value.daysUntil(to.value) + 1;
  if (total <= 0) return 0;

  // Toda semana cheia tem cada dia da semana uma vez; só a sobra é contada um a um.
  const fullWeeks = Math.floor(total / 7);
  let days = fullWeeks * DEFAULT_WORKING_WEEKDAYS.length;
  let cursor = from.value.addDays(fullWeeks * 7);
  for (let index = 0; index < total % 7; index += 1) {
    if (DEFAULT_WORKING_WEEKDAYS.includes(cursor.weekday())) days += 1;
    cursor = cursor.addDays(1);
  }
  return days;
}

/**
 * A equipe que o prazo e as horas-homem sugerem: HH ÷ (dias úteis × 8), numa casa.
 *
 * `null` quando falta o HH ou o prazo — nenhum número é melhor do que um número inventado.
 */
export function impliedCrew(laborHours: number | null, businessDays: number): number | null {
  if (laborHours === null || businessDays <= 0) return null;
  return Math.round((laborHours / (businessDays * HOURS_PER_DAY)) * 10) / 10;
}

/** Dias corridos que o fim previsto passa da linha de base; negativo é adiantado. */
export function slipDays(item: Pick<ScheduleItemView, 'plannedEnd' | 'baselineEnd'>): number {
  const planned = BusinessDate.parse(item.plannedEnd);
  const baseline = BusinessDate.parse(item.baselineEnd);
  if (!planned.ok || !baseline.ok) return 0;
  return baseline.value.daysUntil(planned.value);
}

// ── Agrupamento: torre → pavimento → itens ─────────────────────────────────────────

/**
 * O que a barra de um grupo (o pavimento, a torre) desenha: a janela do previsto e a da
 * linha de base de todos os itens dele, e o avanço do grupo.
 */
export interface BarSummary {
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly baselineStart: string;
  readonly baselineEnd: string;
  /** De 0 a 100, ponderado pelo HH dos itens (ver `groupProgress`). */
  readonly progressPct: number;
}

export interface FloorGroup {
  readonly floorId: string;
  readonly floorName: string;
  readonly level: number;
  readonly items: readonly ScheduleItemView[];
  readonly laborHours: number;
  readonly start: string;
  readonly end: string;
  readonly summary: BarSummary;
}

export interface TowerGroup {
  readonly towerId: string;
  readonly towerName: string;
  readonly floors: readonly FloorGroup[];
  readonly itemCount: number;
  readonly laborHours: number;
  readonly start: string;
  readonly end: string;
  readonly summary: BarSummary;
}

/**
 * O avanço que vale para o item (Fase 34, RN-445; Fase 27, RN-383): os cômodos vencem a
 * medição, e a medição vence o percentual digitado (`itemProgress`). O gravado não muda — a tela
 * escolhe qual mostrar.
 */
export function progressOf(item: ScheduleItemView): number {
  return itemProgress(item).value;
}

/**
 * O avanço de um grupo de itens: a média do avanço de cada um, **pesada pelo HH** — o
 * pavimento que fez a alvenaria (milhares de horas) andou mais do que o que instalou o
 * número do apartamento. Item sem HH (a armação, de empreiteiro) não pesa; se nenhum tem
 * HH, vale a média simples. Uma casa decimal.
 */
export function groupProgress(items: readonly ScheduleItemView[]): number {
  if (items.length === 0) return 0;
  const clamp = (value: number) => Math.min(100, Math.max(0, value));
  let weighted = 0;
  let weights = 0;
  for (const item of items) {
    const weight = Math.max(0, item.laborHours ?? 0);
    weighted += clamp(progressOf(item)) * weight;
    weights += weight;
  }
  const value =
    weights > 0
      ? weighted / weights
      : items.reduce((total, item) => total + clamp(progressOf(item)), 0) / items.length;
  return Math.round(value * 10) / 10;
}

/** A barra de um grupo de itens (não vazio). */
export function summarizeItems(items: readonly ScheduleItemView[]): BarSummary {
  let plannedStart = '9999-12-31';
  let plannedEnd = '0000-01-01';
  let baselineStart = '9999-12-31';
  let baselineEnd = '0000-01-01';
  for (const item of items) {
    plannedStart = earliest(plannedStart, item.plannedStart);
    plannedEnd = latest(plannedEnd, item.plannedEnd);
    baselineStart = earliest(baselineStart, item.baselineStart);
    baselineEnd = latest(baselineEnd, item.baselineEnd);
  }
  return {
    plannedStart,
    plannedEnd,
    baselineStart,
    baselineEnd,
    progressPct: groupProgress(items),
  };
}

function earliest(a: string, b: string): string {
  return a <= b ? a : b;
}

function latest(a: string, b: string): string {
  return a >= b ? a : b;
}

/**
 * Agrupa pela torre e, dentro dela, pelo pavimento **pelo nível** (RN-005) — a ordem do
 * andar, e não a do nome ("10º" depois do "9º"). Dentro do pavimento os itens ficam **pela
 * data** (02/10/2026): o que começa antes — o que já passou — em cima, o futuro embaixo; no
 * mesmo início, o que termina antes; depois disciplina, serviço e trecho.
 *
 * A ordem das torres é a da primeira aparição, que é a do servidor.
 */
export function groupScheduleItems(items: readonly ScheduleItemView[]): TowerGroup[] {
  const towers = new Map<
    string,
    {
      towerName: string;
      floors: Map<string, { floorName: string; level: number; items: ScheduleItemView[] }>;
    }
  >();

  for (const item of items) {
    let tower = towers.get(item.towerId);
    if (tower === undefined) {
      tower = { towerName: item.towerName, floors: new Map() };
      towers.set(item.towerId, tower);
    }
    let floor = tower.floors.get(item.floorId);
    if (floor === undefined) {
      floor = { floorName: item.floorName, level: item.floorLevel, items: [] };
      tower.floors.set(item.floorId, floor);
    }
    floor.items.push(item);
  }

  return [...towers.entries()].map(([towerId, tower]) => {
    const floors: FloorGroup[] = [...tower.floors.entries()]
      .map(([floorId, floor]) => {
        const summary = summarizeItems(floor.items);
        return {
          floorId,
          floorName: floor.floorName,
          level: floor.level,
          items: [...floor.items].sort(byDate),
          laborHours: sumHours(floor.items),
          start: summary.plannedStart,
          end: summary.plannedEnd,
          summary,
        };
      })
      .sort((a, b) => a.level - b.level || a.floorName.localeCompare(b.floorName, 'pt-BR'));

    const summary = summarizeItems(floors.flatMap((floor) => floor.items));
    return {
      towerId,
      towerName: tower.towerName,
      floors,
      itemCount: floors.reduce((total, floor) => total + floor.items.length, 0),
      laborHours: floors.reduce((total, floor) => total + floor.laborHours, 0),
      start: summary.plannedStart,
      end: summary.plannedEnd,
      summary,
    };
  });
}

/** A ordem dos itens dentro do pavimento: pelo início, pelo fim, e então pelo nome. */
function byDate(a: ScheduleItemView, b: ScheduleItemView): number {
  return (
    a.plannedStart.localeCompare(b.plannedStart) ||
    a.plannedEnd.localeCompare(b.plannedEnd) ||
    a.disciplineName.localeCompare(b.disciplineName, 'pt-BR') ||
    (a.serviceName ?? '').localeCompare(b.serviceName ?? '', 'pt-BR') ||
    (a.segment ?? '').localeCompare(b.segment ?? '', 'pt-BR')
  );
}

function sumHours(items: readonly ScheduleItemView[]): number {
  // Centésimos inteiros: somar 677 valores com duas casas em ponto flutuante deixaria 0,0000001.
  return items.reduce((total, item) => total + Math.round((item.laborHours ?? 0) * 100), 0) / 100;
}

// ── Filtros ────────────────────────────────────────────────────────────────────────

export interface ScheduleItemFilter {
  /** Vazio: todas as torres. */
  readonly towerId: string;
  /** Vazio: todas as disciplinas. */
  readonly disciplineId: string;
}

export const NO_FILTER: ScheduleItemFilter = { towerId: '', disciplineId: '' };

export function filterScheduleItems(
  items: readonly ScheduleItemView[],
  filter: ScheduleItemFilter,
): ScheduleItemView[] {
  return items.filter(
    (item) =>
      (filter.towerId === '' || item.towerId === filter.towerId) &&
      (filter.disciplineId === '' || item.disciplineId === filter.disciplineId),
  );
}

export interface FilterOption {
  readonly id: string;
  readonly name: string;
}

/** As torres que os itens citam, na ordem em que aparecem. */
export function towersOf(items: readonly ScheduleItemView[]): FilterOption[] {
  const seen = new Map<string, string>();
  for (const item of items) if (!seen.has(item.towerId)) seen.set(item.towerId, item.towerName);
  return [...seen.entries()].map(([id, name]) => ({ id, name }));
}

/**
 * As disciplinas que os itens citam — das torres escolhidas, quando há filtro de torre,
 * para não oferecer uma disciplina que a torre não tem.
 */
export function disciplinesOf(items: readonly ScheduleItemView[], towerId: string): FilterOption[] {
  const seen = new Map<string, string>();
  for (const item of items) {
    if (towerId !== '' && item.towerId !== towerId) continue;
    if (!seen.has(item.disciplineId)) seen.set(item.disciplineId, item.disciplineName);
  }
  return [...seen.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

// ── A escala do Gantt ──────────────────────────────────────────────────────────────

export interface MonthTick {
  /** `AAAA-MM`. */
  readonly key: string;
  /** `jul/26`. */
  readonly label: string;
  /** Posição do primeiro dia do mês, de 0 a 100. */
  readonly pct: number;
}

export interface Timeline {
  /** Primeiro dia do mês do primeiro item. */
  readonly start: string;
  /** Último dia do mês do último item. */
  readonly end: string;
  /** Dias corridos que a escala cobre. */
  readonly totalDays: number;
  readonly months: readonly MonthTick[];
}

const MONTH_ABBREVIATIONS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;

/**
 * A faixa de tempo que o Gantt cobre: do começo do mês do item mais cedo ao fim do mês do
 * mais tarde, contando a **linha de base** também — um item atrasado sai da janela do previsto
 * e não pode cair para fora do desenho. `undefined` sem itens.
 */
export function timelineOf(
  items: readonly Pick<
    ScheduleItemView,
    'plannedStart' | 'plannedEnd' | 'baselineStart' | 'baselineEnd'
  >[],
): Timeline | undefined {
  if (items.length === 0) return undefined;

  let min = '9999-12-31';
  let max = '0000-01-01';
  for (const item of items) {
    min = earliest(earliest(min, item.plannedStart), item.baselineStart);
    max = latest(latest(max, item.plannedEnd), item.baselineEnd);
  }
  const first = BusinessDate.parse(min);
  const last = BusinessDate.parse(max);
  if (!first.ok || !last.ok) return undefined;

  const start = first.value.startOfMonth();
  const end = last.value.endOfMonth();
  const totalDays = start.daysUntil(end) + 1;

  const months: MonthTick[] = [];
  for (let cursor = start; !cursor.isAfter(end); cursor = cursor.endOfMonth().addDays(1)) {
    months.push({
      key: cursor.monthKey(),
      label: `${MONTH_ABBREVIATIONS[cursor.month - 1] ?? ''}/${String(cursor.year % 100).padStart(2, '0')}`,
      pct: (start.daysUntil(cursor) / totalDays) * 100,
    });
  }

  return { start: start.toString(), end: end.toString(), totalDays, months };
}

/** Onde uma data cai na escala, de 0 a 100. Fora dela, a ponta mais próxima. */
export function positionOn(timeline: Timeline, date: string): number {
  const start = BusinessDate.parse(timeline.start);
  const day = BusinessDate.parse(date);
  if (!start.ok || !day.ok) return 0;
  const pct = (start.value.daysUntil(day.value) / timeline.totalDays) * 100;
  return Math.min(100, Math.max(0, pct));
}

export interface BarGeometry {
  /** Esquerda e largura em porcentagem da escala. */
  readonly left: number;
  readonly width: number;
}

/** Menor largura desenhada: um item de um dia numa obra de dois anos ainda aparece. */
const MIN_BAR_WIDTH_PCT = 0.4;

/** A barra de um período: do começo do primeiro dia ao fim do último, inclusive. */
export function barOn(timeline: Timeline, start: string, end: string): BarGeometry {
  const lastDay = BusinessDate.parse(end);
  const left = positionOn(timeline, start);
  // O fim é inclusivo: a barra vai até o começo do dia seguinte.
  const right = lastDay.ok ? positionOn(timeline, lastDay.value.addDays(1).toString()) : left;
  return { left, width: Math.max(MIN_BAR_WIDTH_PCT, right - left) };
}

/**
 * Os meses que ganham rótulo no eixo: todos, ou de dois em dois, de três em três… conforme
 * o número deles. A faixa do Gantt tem uns 26 rem, e cada rótulo ("jul/26") pede perto de 3
 * rem: cabem oito, e 24 meses viram um rótulo por trimestre.
 */
export function labeledMonths(timeline: Timeline, slots = 8): readonly MonthTick[] {
  const step = Math.max(1, Math.ceil(timeline.months.length / slots));
  return timeline.months.filter((_, index) => index % step === 0);
}

// ── Texto ──────────────────────────────────────────────────────────────────────────

// ── O rótulo da barra, o período e a régua das semanas (redesenho de 21/10/2026) ──

/** `"2026-10-13"` → `"13/10"`, sem zero à esquerda: é o rótulo curto ao lado da barra. */
function dayMonth(date: string): string {
  const [, month, day] = date.split('-');
  return month === undefined || day === undefined
    ? date
    : `${String(Number(day))}/${String(Number(month))}`;
}

/** `13–15/10`, `30/10–3/11`, ou só `13/10` quando o item é de um dia. */
export function formatShortRange(start: string, end: string): string {
  if (start === end) return dayMonth(start);
  return start.slice(0, 7) === end.slice(0, 7)
    ? `${String(Number(start.slice(8, 10)))}–${dayMonth(end)}`
    : `${dayMonth(start)}–${dayMonth(end)}`;
}

/** `13 out a 19 jan`; com o ano quando a janela cruza o ano: `13 out/26 a 19 jan/27`. */
export function formatPeriod(start: string, end: string): string {
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const one = (date: string): string => {
    const month = MONTH_ABBREVIATIONS[Number(date.slice(5, 7)) - 1] ?? '';
    const day = String(Number(date.slice(8, 10)));
    return sameYear ? `${day} ${month}` : `${day} ${month}/${date.slice(2, 4)}`;
  };
  return `${one(start)} a ${one(end)}`;
}

/**
 * O item que já devia ter terminado e não tem avanço nenhum: o sinal âmbar da barra. Só com
 * o hoje — sem ele, não há como saber.
 */
export function isStalled(item: ScheduleItemView, today: string | undefined): boolean {
  return today !== undefined && item.plannedEnd < today && progressOf(item) === 0;
}

export interface WeekTick {
  /** `AAAA-MM-DD` da segunda-feira. */
  readonly key: string;
  /** O dia do mês, sem zero à esquerda. */
  readonly day: string;
  /** Posição, de 0 a 100. */
  readonly pct: number;
}

/**
 * As segundas-feiras da escala: a régua fina sob os meses. Vazia quando a escala é longa
 * demais para as semanas caberem — aí os meses bastam.
 */
export function weekTicks(timeline: Timeline, maxWeeks = 30): readonly WeekTick[] {
  const start = BusinessDate.parse(timeline.start);
  const end = BusinessDate.parse(timeline.end);
  if (!start.ok || !end.ok || timeline.totalDays / 7 > maxWeeks) return [];

  const ticks: WeekTick[] = [];
  let cursor = start.value;
  while (cursor.weekday() !== 1) cursor = cursor.addDays(1);
  for (; !cursor.isAfter(end.value); cursor = cursor.addDays(7)) {
    const key = cursor.toString();
    ticks.push({
      key,
      day: String(Number(key.slice(8, 10))),
      pct: (start.value.daysUntil(cursor) / timeline.totalDays) * 100,
    });
  }
  return ticks;
}

/** O texto que a barra diz para quem não a vê. */
export function describeBar(item: ScheduleItemView): string {
  const slip = slipDays(item);
  const slipText =
    slip > 0
      ? `, termina ${String(slip)} ${slip === 1 ? 'dia' : 'dias'} depois da linha de base`
      : slip < 0
        ? `, termina ${String(-slip)} ${slip === -1 ? 'dia' : 'dias'} antes da linha de base`
        : '';
  return (
    `Previsto de ${formatBusinessDate(item.plannedStart)} a ${formatBusinessDate(item.plannedEnd)}, ` +
    `avanço ${String(progressOf(item))}%${slipText}. ` +
    `Linha de base de ${formatBusinessDate(item.baselineStart)} a ${formatBusinessDate(item.baselineEnd)}.`
  );
}

/** O texto da barra de um grupo — o pavimento, a torre — para quem não a vê. */
export function describeSummaryBar(name: string, summary: BarSummary): string {
  const slip = slipDays(summary);
  const slipText =
    slip > 0
      ? `, termina ${String(slip)} ${slip === 1 ? 'dia' : 'dias'} depois da linha de base`
      : slip < 0
        ? `, termina ${String(-slip)} ${slip === -1 ? 'dia' : 'dias'} antes da linha de base`
        : '';
  return (
    `${name}: previsto de ${formatBusinessDate(summary.plannedStart)} a ${formatBusinessDate(summary.plannedEnd)}, ` +
    `avanço ${formatDecimal(summary.progressPct, 1)}% (pesado pelo HH)${slipText}. ` +
    `Linha de base de ${formatBusinessDate(summary.baselineStart)} a ${formatBusinessDate(summary.baselineEnd)}.`
  );
}

/** `1480.85` vira `"1.480,85"`; `12` vira `"12"` — sem casa que não diz nada. */
export function formatDecimal(value: number, maxDigits = 2): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });
}

/** `"1.234,5 m²"`; sem quantidade, `null`. */
export function formatItemQuantity(
  item: Pick<ScheduleItemView, 'quantity' | 'unit'>,
): string | null {
  if (item.quantity === null) return null;
  const number = formatDecimal(item.quantity, 3);
  return item.unit === null ? number : `${number} ${item.unit}`;
}
